// src/pages/LexiconPractice.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom'; // ✅ THIS LINE IS FIXED
import { auth, db } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';

// --- Exercise 1: Definition Match ---
const DefinitionMatch = ({ terms, onUpdateStatus, onFinish }) => {
  // We need at least 4 terms to make a multiple-choice question.
  const quizTerms = useMemo(() => terms.filter(t => t.status !== 'Mastered'), [terms]);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Build the current question
  const currentQuestion = useMemo(() => {
    if (quizTerms.length === 0) return null;
    
    const questionTerm = quizTerms[currentQuestionIndex];
    
    // Get 3 random wrong terms
    const wrongTerms = quizTerms
      .filter(t => t.id !== questionTerm.id)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
      
    // Shuffle the 1 correct and 3 wrong terms
    const options = [questionTerm, ...wrongTerms].sort(() => 0.5 - Math.random());

    return {
      id: questionTerm.id,
      definition: questionTerm.definition,
      correctAnswer: questionTerm.term,
      options: options.map(t => t.term)
    };
  }, [currentQuestionIndex, quizTerms]);

  if (quizTerms.length < 4) {
    return (
      <div className="py-10 text-center">
        <h3 className="text-2xl font-semibold">🧠 Definition Match</h3>
        <p className="text-gray-600 mt-4">You need to add at least 4 words to your Lexicon to play this game.</p>
        <button onClick={onFinish} className="mt-6 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
          &larr; Back to Practice Menu
        </button>
      </div>
    );
  }

  const handleAnswerClick = (term) => {
    if (isAnswered) return;
    setIsAnswered(true);
    setSelectedAnswer(term);

    if (term === currentQuestion.correctAnswer) {
      setScore(prevScore => prevScore + 1);
      // ✅ Update the word's status in Firestore
      onUpdateStatus(currentQuestion.id, 'In Progress'); 
    }
  };

  const handleNext = () => {
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < quizTerms.length) {
      setCurrentQuestionIndex(nextIndex);
      setIsAnswered(false);
      setSelectedAnswer(null);
    } else {
      setShowResults(true); // End of quiz
    }
  };

  const getButtonClass = (option) => {
    if (!isAnswered) return 'bg-gray-100 hover:bg-gray-200';
    if (option === currentQuestion.correctAnswer) return 'bg-green-500 text-white';
    if (option === selectedAnswer) return 'bg-red-500 text-white';
    return 'bg-gray-100 disabled:opacity-50';
  };

  if (showResults) {
    return (
      <div className="py-10 text-center">
        <h3 className="text-2xl font-semibold">Quiz Results</h3>
        <p className="text-4xl font-bold my-4">{score} / {quizTerms.length}</p>
        <button onClick={onFinish} className="mt-8 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow">
          Back to Practice Menu
        </button>
      </div>
    );
  }

  return (
    <div className="py-10">
      <h3 className="text-2xl font-semibold">🧠 Definition Match</h3>
      <p className="text-gray-500 mb-2">Question {currentQuestionIndex + 1} of {quizTerms.length}</p>
      
      <div className="w-full p-6 bg-indigo-600 text-white rounded-lg shadow-lg my-4">
        <p className="text-sm uppercase">Definition:</p>
        <p className="text-2xl font-bold text-center">
          "{currentQuestion.definition}"
        </p>
      </div>

      <p className="font-semibold mb-2">Which term matches this definition?</p>
      <div className="grid grid-cols-2 gap-3">
        {currentQuestion.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswerClick(option)}
            disabled={isAnswered}
            className={`w-full p-4 text-center rounded-lg transition-colors font-semibold ${getButtonClass(option)}`}
          >
            {option}
          </button>
        ))}
      </div>

      {isAnswered && (
        <button onClick={handleNext} className="mt-6 w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow">
          {currentQuestionIndex === quizTerms.length - 1 ? 'Show Results' : 'Next Question'}
        </button>
      )}
    </div>
  );
};

// --- Main Practice Hub Component ---
const LexiconPractice = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [words, setWords] = useState([]);
  const [loadingWords, setLoadingWords] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'menu', 'def_match', etc.
  const navigate = useNavigate();

  // 1. Fetch user's personal word list
  useEffect(() => {
    if (user) {
      const fetchLexicon = async () => {
        setLoadingWords(true);
        try {
          const lexiconColRef = collection(db, 'users', user.uid, 'lexicon');
          const snapshot = await getDocs(lexiconColRef);
          const wordsArray = snapshot.docs.map(doc => ({
            id: doc.id,
            term: doc.id,
            ...doc.data(),
          }));
          setWords(wordsArray);
        } catch (err) { 
          setError("Failed to fetch your lexicon.");
          console.error(err); 
        }
        setLoadingWords(false);
      };
      fetchLexicon();
    }
  }, [user]);

  // 2. Function to update a word's status in Firestore
  const handleUpdateStatus = async (wordId, newStatus) => {
    if (!user) return;
    try {
      const wordDocRef = doc(db, 'users', user.uid, 'lexicon', wordId);
      await updateDoc(wordDocRef, {
        status: newStatus
      });
      // Update local state to match
      setWords(prevWords => prevWords.map(word => 
        word.id === wordId ? { ...word, status: newStatus } : word
      ));
    } catch (err) {
      console.error("Failed to update word status:", err);
    }
  };

  // --- Render Logic ---

  if (loadingAuth || loadingWords) {
    return <div className="text-center py-20">Loading Your Practice Session...</div>;
  }

  if (!user) {
    // This is the line that was breaking (line 188)
    return <Navigate to="/login" state={{ from: '/lexicon/practice' }} />;
  }

  if (error) {
    return <div className="text-center py-20 bg-red-100 p-5">{error}</div>;
  }

  // If a mode is selected, show that exercise
  if (mode === 'def_match') {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl">
        <DefinitionMatch 
          terms={words} 
          onUpdateStatus={handleUpdateStatus}
          onFinish={() => setMode(null)} 
        />
      </div>
    );
  }

  // Default View: The Practice Menu
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <header className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">Lexicon Practice</h1>
        <p className="text-lg text-gray-600 mt-2">
          You have {words.length} words in your lexicon. Let's master them.
        </p>
        <Link to="/lexicon" className="text-blue-600 hover:underline mt-4 inline-block">
          &larr; Back to Lexicon
        </Link>
      </header>
      
      {words.length === 0 ? (
        <p className="text-center text-gray-500 py-10">Add some words to your Lexicon to start practicing!</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card for Definition Match */}
          <div className="p-6 bg-white shadow rounded-lg">
            <h2 className="text-2xl font-semibold">🧠 Definition Match</h2>
            <p className="text-gray-600 mt-2">We give you a definition, you pick the correct term from your list.</p>
            <button
              onClick={() => setMode('def_match')}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow hover:bg-indigo-700"
            >
              Start Quiz
            </button>
          </div>

          {/* Card for Spelling (Placeholder) */}
          <div className="p-6 bg-white shadow rounded-lg opacity-50">
            <h2 className="text-2xl font-semibold">⌨️ Spelling Recall</h2>
            <p className="text-gray-600 mt-2">We give you the definition, you type the term. (Coming soon!)</p>
            <button
              disabled
              className="mt-4 px-6 py-2 bg-gray-400 text-white font-semibold rounded-lg cursor-not-allowed"
            >
              Start Quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LexiconPractice;